"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var ts_debounce_1 = require("ts-debounce");
var utils_1 = require("./utils");
var DEFAULT_OPTIONS = {
    usePushState: false,
    validateKeysAndTypes: true,
};
var DEBOUNCE_WRITE_URL_MS = 100;
var parseStateFromUrl = function (validator) {
    if (typeof window === 'undefined') {
        return;
    }
    var hash = window.location.hash.slice(1);
    if (!hash) {
        return;
    }
    try {
        var obj = JSON.parse(decodeURIComponent(hash));
        if (!obj) {
            return;
        }
        if (validator) {
            if (validator(obj)) {
                return obj;
            }
            else {
                console.warn('Object in URL hash is invalid, ignoring');
                console.warn(obj);
            }
        }
        else {
            return obj;
        }
    }
    catch (e) {
        console.warn('URL hash is not valid JSON, ignoring');
        // JSON parsing failed
    }
    return undefined;
};
var writeStateToUrl = ts_debounce_1.debounce(function (newState, usePushState) {
    if (usePushState === void 0) { usePushState = false; }
    if (typeof window === 'undefined') {
        return;
    }
    var json = JSON.stringify(newState);
    var title = document.title;
    var url = window.location.pathname + "#" + encodeURIComponent(json);
    if (usePushState) {
        window.history.pushState(undefined, title, url);
    }
    else {
        window.history.replaceState(undefined, title, url);
    }
}, DEBOUNCE_WRITE_URL_MS);
var getKeysAndTypesValidator = function (initialState) {
    var initialStateKeys = new Set(Object.keys(initialState));
    var initialStateTypes = {};
    for (var key in initialState) {
        initialStateTypes[key] = typeof initialState[key];
    }
    return function (obj) {
        if (typeof obj !== 'object' || obj === null) {
            return false;
        }
        var dict = obj;
        if (!utils_1.setsAreEqual(initialStateKeys, new Set(Object.keys(dict)))) {
            return false;
        }
        var objKeys = Object.keys(dict);
        for (var i = 0; i < objKeys.length; i++) {
            var key = objKeys[i];
            var value = dict[key];
            var keyType = typeof value;
            if (keyType !== initialStateTypes[key]) {
                return false;
            }
        }
        return true;
    };
};
var buildValidator = function (initialState, validateKeysAndTypes, customValidator) {
    var validators = [];
    if (validateKeysAndTypes) {
        validators.push(getKeysAndTypesValidator(initialState));
    }
    if (customValidator) {
        validators.push(customValidator);
    }
    return function (obj) { return validators.every(function (v) { return v(obj); }); };
};
var useHashState = function (initialState, options) {
    var _a = __assign(__assign({}, DEFAULT_OPTIONS), options), _b = _a.usePushState, usePushState = _b === void 0 ? false : _b, _c = _a.validateKeysAndTypes, validateKeysAndTypes = _c === void 0 ? true : _c, customValidator = _a.customValidator;
    var didRender = react_1.useRef(false);
    var initialValidator;
    if (!didRender.current) {
        initialValidator = buildValidator(initialState, validateKeysAndTypes, customValidator);
        // Synchronously check the URL hash on the first render,
        // so that the state returned to the caller is the URL state
        // if one is defined.
        var parsedState = parseStateFromUrl(initialValidator);
        if (parsedState) {
            initialState = parsedState;
        }
        else if (initialState) {
            writeStateToUrl(initialState, usePushState);
        }
        didRender.current = true;
    }
    var validatorRef = react_1.useRef(initialValidator);
    var _d = react_1.useState(initialState), state = _d[0], setState = _d[1];
    react_1.useEffect(function () {
        validatorRef.current = buildValidator(initialState, validateKeysAndTypes, customValidator);
    }, [initialState, validateKeysAndTypes, customValidator]);
    var onHashChange = function () {
        var parsedState = parseStateFromUrl(validatorRef.current);
        if (parsedState) {
            setState(parsedState);
        }
    };
    react_1.useEffect(function () {
        window.addEventListener('hashchange', onHashChange);
        return function () {
            window.removeEventListener('hashchange', onHashChange);
        };
    }, []);
    var setStateAtKey = function (key, value) {
        setState(function (prevState) {
            var _a;
            var newState = __assign(__assign({}, prevState), (_a = {}, _a[key] = value, _a));
            writeStateToUrl(newState, usePushState);
            return newState;
        });
    };
    return { state: state, setStateAtKey: setStateAtKey };
};
exports.default = useHashState;
